/**
 * Analytics utility to report events to Google Analytics (GA4)
 * Uses the global gtag function initialized in index.html
 *
 * Event naming follows GA4 conventions:
 *  - GA4 Recommended Events: use Google's predefined names (login, sign_up, etc.)
 *  - Custom Events: use snake_case, prefixed by domain context where helpful
 */

// ─────────────────────────────────────────────
//  GA4 Recommended Events
// ─────────────────────────────────────────────
export const AnalyticsEvents = {
  // Auth
  LOGIN: "login",
  SIGN_UP: "sign_up",

  // Content Discovery
  SEARCH: "search",
  VIEW_ITEM: "view_item", // View a member profile
  VIEW_ITEM_LIST: "view_item_list", // Search results loaded
  SELECT_ITEM: "select_item", // Click "Read More" on a profile card
  SELECT_CONTENT: "select_content", // Nav/footer link click

  // Engagement
  SHARE: "share", // WhatsApp / copy share
  FILE_DOWNLOAD: "file_download", // PDF / Image download

  // Lead Generation
  GENERATE_LEAD: "generate_lead", // Quick Upload form (non-registered lead)
  BEGIN_CHECKOUT: "begin_checkout", // For donation start (future)

  // Forms (Enhanced Measurement compatible)
  FORM_START: "form_start",
  FORM_SUBMIT: "form_submit",
  CONTACT_SUBMIT: "contact_submit",

  // Tutorial / Onboarding flow
  TUTORIAL_BEGIN: "tutorial_begin",
  TUTORIAL_STEP_COMPLETE: "tutorial_step_complete",
  TUTORIAL_COMPLETE: "tutorial_complete",

  // Clicks
  CLICK: "click", // Generic click event
  OUTBOUND_CLICK: "outbound_click",

  // ─────────────────────────────────────────────
  //  Custom Events (Kalyanawaran-specific)
  // ─────────────────────────────────────────────

  // Profile actions
  PROFILE_SAVE: "profile_save",
  PROFILE_IMAGE_UPLOAD: "profile_image_upload",
  PROFILE_STATUS_CHANGE: "profile_status_change",
  ACCOUNT_SAVE: "account_save",

  // Horoscope sharing
  HOROSCOPE_SHARE_WHATSAPP: "horoscope_share_whatsapp",
  HOROSCOPE_COPY_LINK: "horoscope_copy_link",
  HOROSCOPE_COPY_MESSAGE: "horoscope_copy_message",

  // Interest / Matchmaking
  INTEREST_SENT: "interest_sent",
  INTEREST_REMOVED: "interest_removed",
  INTEREST_MUTUAL: "interest_mutual",

  // Password & Account
  PASSWORD_RESET_REQUEST: "password_reset_request",
  PASSWORD_CHANGED: "password_changed",
  LOGOUT: "logout",
  EMAIL_VERIFY_REQUEST: "email_verify_request",

  // Search refinement
  FILTER_APPLIED: "filter_applied",
  SORT_APPLIED: "sort_applied",
};

/**
 * Tracks a custom event in Google Analytics
 * @param {string} eventName - The name of the event (e.g., 'login', 'search')
 * @param {object} eventParams - Optional parameters for the event
 */
export const trackEvent = (eventName, eventParams = {}) => {
  if (typeof window.gtag === "function") {
    window.gtag("event", eventName, eventParams);
  } else {
    console.warn("Google Analytics (gtag) is not initialized.");
  }
};

/**
 * Tracks a page view in Google Analytics
 * @param {string} path - The path of the page viewed
 * @param {string} pageTitle - The title of the page
 * @param {string} screenName - The screen name
 */
export const trackPageView = (path, pageTitle, screenName) => {
  if (typeof window.gtag === "function") {
    // GA4 automatically tracks views, but for SPA we usually need to update the path
    window.gtag("event", "page_view", {
      page_path: path,
      page_title: pageTitle,
      screen_name: screenName || pageTitle,
      send_to: process.env.REACT_APP_GTM_ID,
    });
  }
};

/**
 * Sets the User ID and User Properties
 * @param {string} userId - The User ID
 * @param {object} userProperties - Additional user properties (e.g. { username: '...' })
 */
export const identifyUser = (userId, userProperties = {}) => {
  if (typeof window.gtag === "function") {
    // Set User ID (applied to future events)
    window.gtag("config", process.env.REACT_APP_GTM_ID, {
      user_id: userId,
    });

    // Set User Properties
    if (Object.keys(userProperties).length > 0) {
      window.gtag("set", "user_properties", userProperties);
    }
  }
};

/**
 * Helper to track button clicks
 * @param {string} buttonName - Name of the button clicked
 * @param {string} location - Where the button is located (e.g., 'header')
 */
export const trackButtonClick = (buttonName, location) => {
  trackEvent(AnalyticsEvents.CLICK, {
    event_category: "button",
    event_label: buttonName,
    location: location,
  });
};

/**
 * Helper to track share actions (GA4 recommended `share` event)
 * @param {string} method - Share method (e.g., 'whatsapp', 'copy_link', 'copy_message')
 * @param {string} contentType - Type of content shared (e.g., 'profile', 'horoscope')
 * @param {string} itemId - ID of the shared item
 */
export const trackShare = (method, contentType, itemId) => {
  trackEvent(AnalyticsEvents.SHARE, {
    method: method,
    content_type: contentType,
    item_id: itemId,
  });
};

/**
 * Detects device information from the User Agent string.
 * NOTE: GA4 auto-collects device category, OS, browser, and screen resolution
 * in its built-in dimensions. These custom user properties are for easier
 * custom segmentation in GA4 Explore reports without needing BigQuery.
 *
 * @returns {{ device_type: string, os: string, browser: string, screen_resolution: string }}
 */
export const getDeviceInfo = () => {
  const ua = navigator.userAgent || "";

  // Device type
  let device_type = "desktop";
  if (/Mobi|Android/i.test(ua)) {
    device_type = /Tablet|iPad/i.test(ua) ? "tablet" : "mobile";
  } else if (/iPad/i.test(ua)) {
    device_type = "tablet";
  }

  // Operating System
  let os = "unknown";
  if (/Windows/i.test(ua)) os = "Windows";
  else if (/Mac OS|Macintosh/i.test(ua)) os = /Mobi/i.test(ua) ? "iOS" : "macOS";
  else if (/iPhone|iPad|iPod/i.test(ua)) os = "iOS";
  else if (/Android/i.test(ua)) os = "Android";
  else if (/Linux/i.test(ua)) os = "Linux";
  else if (/CrOS/i.test(ua)) os = "ChromeOS";

  // Browser
  let browser = "unknown";
  if (/Edg\//i.test(ua)) browser = "Edge";
  else if (/OPR|Opera/i.test(ua)) browser = "Opera";
  else if (/Chrome/i.test(ua) && !/Edg/i.test(ua)) browser = "Chrome";
  else if (/Safari/i.test(ua) && !/Chrome/i.test(ua)) browser = "Safari";
  else if (/Firefox/i.test(ua)) browser = "Firefox";

  // Screen resolution
  const screen_resolution = `${window.screen.width}x${window.screen.height}`;

  return { device_type, os, browser, screen_resolution };
};

/**
 * Calculates age from a date-of-birth string.
 * @param {string} dateString - Date of birth (ISO or parseable date string)
 * @returns {number|null} The calculated age, or null if the date is invalid
 */
export const calculateAge = (dateString) => {
  if (!dateString) return null;
  const birthDate = new Date(dateString);
  if (isNaN(birthDate.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age > 0 && age < 120 ? age : null;
};
